import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  TablePagination,
  Grid,
} from "@mui/material";
import {
  FaBuilding,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaPhone,
  FaMapMarkerAlt,
} from "react-icons/fa";
import {
  getCompanies,
  createCompany,
  getCompanyDetails,
  updateCompany,
  deleteCompany,
  selectCompanyManagement,
  CompanyManagementState,
  resetCompanyManagement,
  clearCurrentCompany,
} from "../slices/company-management.slice";
import { Company } from "features/ms6/core/ms6.param";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { getCompanyUserPermissionsRepository } from "features/ms6/data/ms6.repository";

export function CompanyManagement() {
  const dispatch = useAppDispatch();
  const companyState = useAppSelector(selectCompanyManagement);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const { is_admin } = getAdminSessionState.data?.admin || {};

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const [userPermissions, setUserPermissions] = useState({
    is_admin: false,
    is_gc: false,
    can_view: false,
    can_edit: false,
    can_delete: false,
  });

  // Simplified form data - only 3 fields
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    contact_number: "",
  });

  useEffect(() => {
    if (userPermissions.can_view) {
      dispatch(getCompanies());
    }
  }, [dispatch, userPermissions.can_view]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await getCompanyUserPermissionsRepository();
        setUserPermissions(response.data.data);
      } catch (error) {
        console.error("Failed to fetch user permissions:", error);
      }
    };

    fetchPermissions();
  }, []);

  useEffect(() => {
    if (companyState.status === CompanyManagementState.success) {
      setTimeout(() => {
        dispatch(resetCompanyManagement());
      }, 3000);
    }
  }, [companyState.status, dispatch]);

  const handleCreateClick = () => {
    setFormData({
      name: "",
      address: "",
      contact_number: "",
    });
    setCreateDialogOpen(true);
  };

  const handleEditClick = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      address: company.address || "",
      contact_number: company.contact_number || "",
    });
    setEditDialogOpen(true);
  };

  const handleViewClick = (company: Company) => {
    setSelectedCompany(company);
    dispatch(getCompanyDetails(company.id));
    setViewDialogOpen(true);
  };

  const handleDeleteClick = (company: Company) => {
    setSelectedCompany(company);
    setDeleteDialogOpen(true);
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const submitCreate = () => {
    if (!formData.name.trim()) {
      return;
    }

    dispatch(createCompany(formData)).then((result) => {
      if (result.type.endsWith("/fulfilled")) {
        setCreateDialogOpen(false);
        dispatch(getCompanies());
      }
    });
  };

  const submitEdit = () => {
    if (!selectedCompany || !formData.name.trim()) {
      return;
    }

    dispatch(
      updateCompany({ companyId: selectedCompany.id, companyData: formData })
    ).then((result) => {
      if (result.type.endsWith("/fulfilled")) {
        setEditDialogOpen(false);
        setSelectedCompany(null);
        dispatch(getCompanies());
      }
    });
  };

  const confirmDelete = () => {
    if (!selectedCompany) return;

    dispatch(deleteCompany(selectedCompany.id)).then((result) => {
      if (result.type.endsWith("/fulfilled")) {
        setDeleteDialogOpen(false);
        setSelectedCompany(null);
        dispatch(getCompanies());
      }
    });
  };

  const paginatedCompanies = companyState.companies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (!userPermissions.can_view) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Alert severity="error">
          Access denied. Administrator or GC privileges required.
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <FaBuilding className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Company Management
              </h1>
              <p className="text-gray-600">
                Manage companies for sponsorship gift certificates
              </p>
            </div>
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FaPlus />}
            onClick={handleCreateClick}
            size="large"
          >
            Add New Company
          </Button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {companyState.status === CompanyManagementState.success && (
        <Alert severity="success" className="mb-4">
          {companyState.message}
        </Alert>
      )}

      {companyState.status === CompanyManagementState.fail && (
        <Alert severity="error" className="mb-4">
          {companyState.message}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} className="mb-6">
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-gray-900">
                    {companyState.companies.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Companies
                  </Typography>
                </div>
                <FaBuilding size={32} className="text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-blue-600">
                    {companyState.companies.reduce(
                      (sum, c) => sum + c.gc_count,
                      0
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Gift Certificates
                  </Typography>
                </div>
                <FaBuilding size={32} className="text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-green-600">
                    {
                      companyState.companies.filter((c) => c.contact_number)
                        .length
                    }
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    With Contact Numbers
                  </Typography>
                </div>
                <FaPhone size={32} className="text-green-500" />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Companies Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Company Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Contact Number</TableCell>
                <TableCell>Gift Certificates</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCompanies.map((company) => (
                <TableRow key={company.id} hover>
                  <TableCell>
                    <Typography variant="body1" className="font-semibold">
                      {company.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {company.address ? (
                      <div className="flex items-center">
                        <FaMapMarkerAlt
                          size={14}
                          className="mr-2 text-gray-400"
                        />
                        <span className="text-sm">
                          {company.address.length > 50
                            ? `${company.address.substring(0, 50)}...`
                            : company.address}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {company.contact_number ? (
                      <div className="flex items-center">
                        <FaPhone size={14} className="mr-2 text-gray-400" />
                        <span className="text-sm">
                          {company.contact_number}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={company.gc_count}
                      size="small"
                      color={company.gc_count > 0 ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-1">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewClick(company)}
                          color="primary"
                        >
                          <FaEye size={14} />
                        </IconButton>
                      </Tooltip>

                      {/* Only show edit/delete for non-GC users */}
                      {userPermissions.can_edit && (
                        <>
                          <Tooltip title="Edit Company">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(company)}
                              color="primary"
                            >
                              <FaEdit size={14} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              company.gc_count > 0
                                ? `Cannot delete - ${company.gc_count} gift certificates exist`
                                : "Delete Company"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(company)}
                                color="error"
                                disabled={company.gc_count > 0}
                              >
                                <FaTrash size={14} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={companyState.companies.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Create Company Dialog - Simplified */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaPlus className="text-purple-500" />
            <span>Add New Company</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2 space-y-4">
            <TextField
              fullWidth
              label="Company Name"
              value={formData.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              placeholder="Enter company name"
              required
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Address"
              value={formData.address}
              onChange={(e) => handleFormChange("address", e.target.value)}
              placeholder="Enter company address"
            />
            <TextField
              fullWidth
              label="Contact Number"
              type="tel"
              value={formData.contact_number}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/[^0-9]/g, "");
                handleFormChange("contact_number", value);
              }}
              placeholder="Enter contact number (optional)"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={submitCreate}
            variant="contained"
            color="primary"
            disabled={!formData.name.trim()}
            startIcon={<FaPlus />}
          >
            Create Company
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Company Dialog - Simplified */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaEdit className="text-blue-500" />
            <span>Edit Company</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2 space-y-4">
            <TextField
              fullWidth
              label="Company Name *"
              value={formData.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              placeholder="Enter company name"
              required
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Address"
              value={formData.address}
              onChange={(e) => handleFormChange("address", e.target.value)}
              placeholder="Enter company address"
            />
            <TextField
              fullWidth
              label="Contact Number"
              type="tel"
              value={formData.contact_number}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/[^0-9]/g, "");
                handleFormChange("contact_number", value);
              }}
              placeholder="Enter contact number (optional)"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={submitEdit}
            variant="contained"
            color="primary"
            disabled={!formData.name.trim()}
            startIcon={<FaEdit />}
          >
            Update Company
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Company Dialog - Simplified */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          dispatch(clearCurrentCompany());
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaEye className="text-green-500" />
            <span>Company Details</span>
          </div>
        </DialogTitle>
        <DialogContent>
          {selectedCompany && (
            <div className="pt-2">
              <Card className="bg-gray-50">
                <CardContent>
                  <Typography variant="h5" className="font-bold mb-4">
                    {selectedCompany.name}
                  </Typography>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <FaMapMarkerAlt className="text-gray-500" />
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {selectedCompany.address || "No address provided"}
                        </Typography>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaPhone className="text-gray-500" />
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Contact Number
                        </Typography>
                        <Typography variant="body1">
                          {selectedCompany.contact_number ||
                            "No contact number provided"}
                        </Typography>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaBuilding className="text-gray-500" />
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Gift Certificates
                        </Typography>
                        <Typography variant="body1">
                          {selectedCompany.gc_count} certificate
                          {selectedCompany.gc_count !== 1 ? "s" : ""}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              dispatch(clearCurrentCompany());
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Company Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaTrash className="text-red-500" />
            <span>Delete Company</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <Alert severity="warning" className="mb-4">
              <Typography variant="body2">
                <strong>Warning:</strong> This action cannot be undone.
                {selectedCompany?.gc_count === 0
                  ? " The company will be permanently deleted."
                  : ` Cannot delete company with ${selectedCompany?.gc_count} existing gift certificates.`}
              </Typography>
            </Alert>

            {selectedCompany && (
              <Card className="bg-gray-50">
                <CardContent>
                  <Typography variant="h6" className="mb-2">
                    Company Details
                  </Typography>
                  <div className="space-y-2">
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Company Name
                      </Typography>
                      <Typography variant="body2" className="font-semibold">
                        {selectedCompany.name}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Gift Certificates
                      </Typography>
                      <Chip
                        label={selectedCompany.gc_count}
                        size="small"
                        color={
                          selectedCompany.gc_count > 0 ? "error" : "default"
                        }
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={selectedCompany?.gc_count !== 0}
            startIcon={<FaTrash />}
          >
            Delete Company
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
